import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, AlertCircle, Loader2, RefreshCw,
  TrendingUp, TrendingDown, Skull, LineChart, MessageSquare, ScanLine, Layers, Target,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const SEVERITY_STYLES = {
  critical: { bg: "#FEF2F2", border: "#FF2A00", text: "#FF2A00", label: "CRITICAL" },
  high: { bg: "#FEF2F2", border: "#FF2A00", text: "#CC2200", label: "HIGH" },
  medium: { bg: "#FFFBEB", border: "#FFC800", text: "#92400E", label: "MEDIUM" },
  low: { bg: "#F4F4F5", border: "#A1A1AA", text: "#52525B", label: "LOW" },
};

const STATUS_COLORS = { good: "#00C853", neutral: "#A1A1AA", bad: "#FF2A00" };

const TABS = [
  { id: "overview", label: "Overview", icon: Target },
  { id: "flags", label: "Red Flags", icon: Skull },
  { id: "financials", label: "Financials", icon: LineChart },
  { id: "forensic", label: "Forensic", icon: ScanLine },
  { id: "management", label: "Management", icon: MessageSquare },
  { id: "footnotes", label: "Footnotes", icon: Layers },
  { id: "valuation", label: "Valuation", icon: AlertCircle },
];

export default function Report() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [reanalyzing, setReanalyzing] = useState(false);
  const [tickerInput, setTickerInput] = useState("");
  const [attaching, setAttaching] = useState(false);
  // Mirrors `report` for the polling loop below — see the effect for why a
  // plain ref (rather than reading React state inside setInterval) is used.
  const statusRef = useRef(null);

  const reanalyze = async () => {
    if (!window.confirm("Re-run the AI analysis with the latest prompt? This may take 1-3 minutes.")) return;
    setReanalyzing(true);
    try {
      await api.post(`/reports/${id}/analyze`, { model: "gemini-2.5-flash", force: true });
      toast.success("Re-analysis started — the report will refresh automatically.");
      setReport((r) => {
        const next = r ? { ...r, analyzed: false, analysis: null, analysis_status: "processing" } : r;
        statusRef.current = next;
        return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to trigger re-analysis");
    } finally {
      setReanalyzing(false);
    }
  };

  const attachTicker = async () => {
    const t = tickerInput.trim().toUpperCase();
    if (!t) return;
    setAttaching(true);
    try {
      await api.post(`/reports/${id}/attach-ticker`, { ticker: t });
      toast.success(`Ticker ${t} attached. Click Re-analyze to inject the live price.`);
      setReport((r) => (r ? { ...r, ticker: t } : r));
      setTickerInput("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to attach ticker");
    } finally {
      setAttaching(false);
    }
  };


  useEffect(() => {
    let active = true;
    const fetchOnce = () =>
      api.get(`/reports/${id}`)
        .then((r) => {
          if (!active) return;
          statusRef.current = r.data;
          setReport(r.data);
        })
        .catch(() => active && toast.error("Failed to load"))
        .finally(() => active && setLoading(false));

    fetchOnce();
    const interval = setInterval(() => {
      const curr = statusRef.current;
      const done = curr && curr.analyzed && curr.analysis_status !== "processing";
      const errored = curr && curr.analysis_status === "error";
      if (done || errored) return;
      fetchOnce();
    }, 4000);
    return () => { active = false; clearInterval(interval); };
  }, [id]);

  if (loading) return <Page><div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></Page>;
  if (!report) return <Page><div>Not found.</div></Page>;

  if (!report.analyzed || !report.analysis) {
    const isProcessing = report.analysis_status === "processing";
    const errored = report.analysis_status === "error";
    return (
      <Page>
        <div className="border-2 border-strong p-8 bg-white max-w-2xl shadow-brutal">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">/ {isProcessing ? "Analyzing" : errored ? "Failed" : "Pending"}</div>
          <div className="font-display font-bold text-3xl tracking-tight mb-3">{report.title}</div>
          {isProcessing && (
            <>
              <div className="flex items-center gap-3 text-sm text-zinc-700">
                <Loader2 className="w-5 h-5 animate-spin" /> AI is dissecting the report. This page will auto-refresh.
              </div>
              <div className="text-xs text-zinc-500 mt-3">Large annual reports can take 1-3 minutes.</div>
            </>
          )}
          {errored && (
            <>
              <div className="text-sm text-[#FF2A00] mb-3">{report.analysis_error || "Unknown error"}</div>
              <Link to="/app/analyze" className="inline-block bg-black text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#002FA7]">Try again</Link>
            </>
          )}
        </div>
      </Page>
    );
  }

  const a = report.analysis;
  const snap = a.company_snapshot || {};
  const healthScore = Number(snap.overall_health_score || 0);
  const flags = a.red_flags || [];
  const flagCounts = flags.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <Page>
      <div className="flex items-center justify-between mb-6">
        <Link to="/app" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-black" data-testid="report-back">
          <ArrowLeft className="w-3 h-3" /> Back to Dashboard
        </Link>
        <button
          onClick={reanalyze}
          disabled={reanalyzing}
          data-testid="report-reanalyze"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border-2 border-strong bg-white px-3 py-2 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${reanalyzing ? "animate-spin" : ""}`} />
          {reanalyzing ? "Requesting…" : "Re-analyze"}
        </button>
      </div>

      {/* ── STICKY HEADER ─────────────────────────────────────── */}
      <div className="grid lg:grid-cols-12 gap-0 border-2 border-strong bg-white mb-6 shadow-brutal">
        <div className="lg:col-span-8 p-8 border-r-0 lg:border-r-2 border-strong">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
            / {snap.period || "Report"}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.9] mb-4">
            {snap.name || report.title}
          </h1>
          {snap.one_line_thesis && (
            <div className="border-l-4 border-[#002FA7] bg-[#F0F4FF] px-4 py-3 mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] mb-1">Thesis</div>
              <div className="text-sm font-bold leading-snug">{snap.one_line_thesis}</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 text-xs uppercase tracking-[0.2em]">
            {snap.sector && <span className="border border-strong px-3 py-1">{snap.sector}</span>}
            {snap.currency && <span className="border border-strong px-3 py-1">{snap.currency}</span>}
            {snap.auditor && <span className="border border-strong px-3 py-1">Auditor · {snap.auditor}</span>}
            {snap.verdict && <span className="bg-black text-white px-3 py-1">{snap.verdict}</span>}
          </div>
        </div>
        <div className="lg:col-span-4 p-8 flex flex-col justify-center items-center bg-black text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">Health Score</div>
          <div className={`font-display font-black text-8xl tracking-tighter leading-none ${healthScore < 40 ? "text-[#FF2A00]" : healthScore < 70 ? "text-[#FFC800]" : "text-[#00C853]"}`}>
            {healthScore}
          </div>
          <div className="text-xs text-zinc-400 mt-2">/ 100</div>
          <div className="w-full h-2 bg-zinc-800 mt-6">
            <div className={`h-2 ${healthScore < 40 ? "bg-[#FF2A00]" : healthScore < 70 ? "bg-[#FFC800]" : "bg-[#00C853]"}`} style={{ width: `${healthScore}%` }} />
          </div>
        </div>
      </div>

      {/* ── LIVE MARKET QUOTE OR ATTACH-TICKER ───────────────── */}
      {report.live_quote ? (
        <div className="border-2 border-strong bg-white mb-6 p-5 grid md:grid-cols-4 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Live Price</div>
            <div className="font-display font-black text-3xl tracking-tighter font-mono-data">{report.live_quote.price}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{report.live_quote.ticker} · {report.live_quote.latest_trading_day}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Change</div>
            <div className={`font-display font-black text-2xl tracking-tighter font-mono-data ${String(report.live_quote.change || "").startsWith("-") ? "text-[#FF2A00]" : "text-[#00C853]"}`}>
              {report.live_quote.change} <span className="text-sm">({report.live_quote.change_pct})</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Day Range</div>
            <div className="font-mono-data text-sm">{report.live_quote.low} — {report.live_quote.high}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Prev close {report.live_quote.previous_close}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Volume</div>
            <div className="font-mono-data text-sm">{Number(report.live_quote.volume).toLocaleString()}</div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-strong bg-white mb-6 p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Attach Live Price</div>
            <div className="text-sm text-zinc-700">
              Add a ticker so the DCF/Multiples engine can anchor to <b>today&apos;s actual market price</b> instead of guessing.
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <input
              value={tickerInput}
              onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
              placeholder="e.g. RELIANCE.BSE"
              maxLength={15}
              data-testid="attach-ticker-input"
              className="border-2 border-strong px-3 py-2 text-sm focus:border-[#002FA7] outline-none uppercase font-mono-data w-48"
            />
            <button
              onClick={attachTicker}
              disabled={attaching || !tickerInput.trim()}
              data-testid="attach-ticker-btn"
              className="bg-black text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#002FA7] disabled:opacity-50"
            >
              {attaching ? "…" : "Attach"}
            </button>
          </div>
        </div>
      )}

      {/* ── QUICK STATS STRIP ─────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-strong bg-white mb-6">
        <Stat label="Red Flags" value={flags.length} accent="#FF2A00" />
        <Stat label="Critical" value={flagCounts.critical || 0} />
        <Stat label="Ratios" value={(a.key_ratios || []).length} />
        <Stat label="Footnotes" value={(a.minute_details || []).length} />
      </div>

      {/* ── STICKY TAB BAR ────────────────────────────────────── */}
      <div className="sticky top-0 z-30 -mx-6 px-6 bg-[#F4F4F5]/95 backdrop-blur-sm border-b-2 border-strong mb-8">
        <div className="flex overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex-shrink-0 inline-flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-[0.2em] border-b-2 -mb-[2px] transition-colors ${
                  active ? "border-[#FF2A00] text-black font-bold" : "border-transparent text-zinc-500 hover:text-black"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
                {t.id === "flags" && flags.length > 0 && (
                  <span className="bg-[#FF2A00] text-white text-[9px] px-1.5 py-0.5 rounded-full">{flags.length}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── TAB CONTENT ───────────────────────────────────────── */}
      <div className="pb-16">
        {tab === "overview" && <OverviewTab a={a} snap={snap} flags={flags} />}
        {tab === "flags" && <FlagsTab flags={flags} />}
        {tab === "financials" && <FinancialsTab a={a} />}
        {tab === "forensic" && <ForensicTab fs={a.forensic_signals} />}
        {tab === "management" && <ManagementTab m={a.management_commentary_analysis} />}
        {tab === "footnotes" && <FootnotesTab items={a.minute_details} />}
        {tab === "valuation" && <ValuationTab v={a.valuation_view} />}

        {/* Bottom line always visible */}
        <div className="border-2 border-strong bg-black text-white p-8 mt-12" data-testid="bottom-line">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3">/ Bottom Line</div>
          <p className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight">{a.bottom_line}</p>
          <div className="text-xs text-zinc-500 mt-6">
            FinLens analysis is for educational purposes only. Not investment advice.
          </div>
        </div>
      </div>
    </Page>
  );
}

/* ─────────────────────────── TAB PANELS ─────────────────────────── */

function OverviewTab({ a, snap, flags }) {
  const topFlags = [...flags].sort((x, y) => sevRank(x.severity) - sevRank(y.severity)).slice(0, 3);
  const acc = a.accuracy_notes || {};
  return (
    <div className="space-y-8" data-testid="tab-panel-overview">
      <Panel title="Executive Summary">
        <p className="text-base text-zinc-800 leading-relaxed">{a.executive_summary}</p>
      </Panel>
      {topFlags.length > 0 && (
        <Panel title="Top 3 Red Flags" accent="#FF2A00">
          <div className="space-y-3">
            {topFlags.map((f, i) => <FlagCard key={i} f={f} />)}
          </div>
        </Panel>
      )}
      {snap.verdict && (
        <div className="grid md:grid-cols-2 gap-0 border-2 border-strong bg-white">
          <div className="p-6 border-r-0 md:border-r-2 border-strong">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Verdict</div>
            <div className="font-display font-black text-3xl tracking-tighter">{snap.verdict}</div>
          </div>
          <div className="p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Period · Currency</div>
            <div className="text-base font-bold">
              {snap.period || "—"} · {snap.currency || "—"}
            </div>
          </div>
        </div>
      )}
      {(acc.coverage_confidence != null || (acc.data_gaps || []).length > 0 || (acc.would_need_to_confirm || []).length > 0) && (
        <Panel title="Analysis Accuracy" accent="#FFC800">
          <div className="border-2 border-strong bg-white p-6 space-y-4">
            {acc.coverage_confidence != null && (
              <div>
                <div className="flex items-baseline justify-between text-xs mb-2">
                  <span className="uppercase tracking-[0.2em] text-zinc-500">Coverage Confidence</span>
                  <span className="font-mono-data font-bold">{acc.coverage_confidence}%</span>
                </div>
                <div className="w-full h-1.5 bg-zinc-200">
                  <div className={`h-1.5 ${acc.coverage_confidence < 40 ? "bg-[#FF2A00]" : acc.coverage_confidence < 70 ? "bg-[#FFC800]" : "bg-[#00C853]"}`} style={{ width: `${acc.coverage_confidence}%` }} />
                </div>
                <div className="text-xs text-zinc-500 mt-2">How much of the report we could ground findings in.</div>
              </div>
            )}
            {(acc.data_gaps || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Data Gaps</div>
                <ul className="text-sm space-y-1">
                  {acc.data_gaps.map((g, i) => (
                    <li key={i} className="flex gap-2"><span className="text-[#FFC800]">▸</span>{g}</li>
                  ))}
                </ul>
              </div>
            )}
            {(acc.would_need_to_confirm || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Human should verify</div>
                <ul className="text-sm space-y-1">
                  {acc.would_need_to_confirm.map((g, i) => (
                    <li key={i} className="flex gap-2"><span className="text-[#002FA7]">▸</span>{g}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}

function FlagsTab({ flags }) {
  if (!flags.length) {
    return (
      <div className="border-2 border-strong bg-white p-8 flex items-center gap-4">
        <CheckCircle2 className="w-6 h-6 text-[#00C853]" />
        <div>
          <div className="font-bold">No major red flags detected.</div>
          <div className="text-xs text-zinc-500">Still review the full report — no automated tool catches everything.</div>
        </div>
      </div>
    );
  }
  const grouped = flags.reduce((acc, f) => {
    (acc[f.severity] = acc[f.severity] || []).push(f);
    return acc;
  }, {});
  return (
    <div className="space-y-8" data-testid="tab-panel-flags">
      {["critical", "high", "medium", "low"].map((sev) => {
        const list = grouped[sev];
        if (!list?.length) return null;
        const sty = SEVERITY_STYLES[sev];
        return (
          <div key={sev}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-3 h-3" style={{ background: sty.border }} />
              <h3 className="font-display font-black text-2xl tracking-tighter">{sty.label} <span className="text-zinc-400 font-normal text-base">· {list.length}</span></h3>
            </div>
            <div className="space-y-3">
              {list.map((f, i) => <FlagCard key={i} f={f} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FinancialsTab({ a }) {
  return (
    <div className="space-y-10" data-testid="tab-panel-financials">
      {/* HIGHLIGHTS */}
      {(a.financial_highlights || []).length > 0 && (
        <Panel title="Financial Highlights" count={a.financial_highlights.length}>
          <div className="border-2 border-strong bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 border-b-2 border-strong">
                <tr className="text-[10px] uppercase tracking-[0.2em]">
                  <th className="text-left px-4 py-3">Metric</th>
                  <th className="text-right px-4 py-3">Value</th>
                  <th className="text-right px-4 py-3">YoY</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Commentary</th>
                </tr>
              </thead>
              <tbody>
                {a.financial_highlights.map((h, i) => {
                  const yoy = String(h.yoy_change || "");
                  const isNeg = yoy.startsWith("-");
                  const positive = yoy.includes("+") || (!isNeg && /\d/.test(yoy));
                  return (
                    <tr key={i} className="border-b border-zinc-200">
                      <td className="px-4 py-3 font-medium">{h.metric}</td>
                      <td className="px-4 py-3 text-right font-mono-data">{h.value}</td>
                      <td className={`px-4 py-3 text-right font-mono-data ${isNeg ? "text-[#FF2A00]" : positive ? "text-[#00C853]" : ""}`}>
                        <span className="inline-flex items-center gap-1">
                          {isNeg ? <TrendingDown className="w-3 h-3" /> : positive ? <TrendingUp className="w-3 h-3" /> : null}
                          {h.yoy_change}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-600 hidden md:table-cell">{h.commentary}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* KEY RATIOS */}
      {(a.key_ratios || []).length > 0 && (
        <Panel title="Key Ratios" count={a.key_ratios.length}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-strong">
            {a.key_ratios.map((r, i) => (
              <div key={i} className="border border-strong -mt-px -ml-px p-5 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{r.name}</div>
                  <div className="w-2 h-2" style={{ background: STATUS_COLORS[r.status] || "#A1A1AA" }} />
                </div>
                <div className="font-display font-bold text-3xl tracking-tighter font-mono-data">{r.value}</div>
                <div className="text-xs text-zinc-500 mt-1">vs {r.benchmark}</div>
                <div className="text-xs text-zinc-700 mt-3 leading-relaxed">{r.explanation}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* YoY / QoQ */}
      {(a.yoy_qoq_comparisons || []).length > 0 && (
        <Panel title="YoY / QoQ Comparisons" count={a.yoy_qoq_comparisons.length}>
          <div className="border-2 border-strong bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 border-b-2 border-strong">
                <tr className="text-[10px] uppercase tracking-[0.2em]">
                  <th className="text-left px-4 py-3">Metric</th>
                  <th className="text-right px-4 py-3">Current</th>
                  <th className="text-right px-4 py-3">Previous</th>
                  <th className="text-right px-4 py-3">Δ</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Interpretation</th>
                </tr>
              </thead>
              <tbody>
                {a.yoy_qoq_comparisons.map((c, i) => {
                  const isNeg = String(c.change_pct).startsWith("-");
                  return (
                    <tr key={i} className="border-b border-zinc-200">
                      <td className="px-4 py-3 font-medium">{c.metric}</td>
                      <td className="px-4 py-3 text-right font-mono-data">{c.current}</td>
                      <td className="px-4 py-3 text-right font-mono-data text-zinc-500">{c.previous}</td>
                      <td className={`px-4 py-3 text-right font-mono-data font-bold ${isNeg ? "text-[#FF2A00]" : "text-[#002FA7]"}`}>{c.change_pct}</td>
                      <td className="px-4 py-3 text-xs text-zinc-600 hidden md:table-cell">{c.interpretation}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function ForensicTab({ fs }) {
  if (!fs) return <EmptyPanel note="No forensic-signal data returned for this report." />;
  const cards = [
    ["Cash vs Earnings", fs.cash_vs_earnings],
    ["Working Capital", fs.working_capital_trend],
    ["Capital Allocation", fs.capital_allocation],
    ["Contingent Liabilities", fs.contingent_liabilities],
    ["Auditor Changes", fs.auditor_changes],
  ].filter(([_, v]) => v);
  return (
    <div className="space-y-8" data-testid="tab-panel-forensic">
      {cards.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-strong bg-white">
          {cards.map(([label, value], i) => (
            <div key={i} className="border border-strong -mt-px -ml-px p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
              <div className="text-sm leading-relaxed text-zinc-800">{value}</div>
            </div>
          ))}
        </div>
      )}
      {(fs.accounting_policy_changes || []).length > 0 && (
        <Panel title="Accounting Policy Changes" accent="#FF2A00">
          <ul className="border-2 border-strong bg-white divide-y divide-zinc-200">
            {fs.accounting_policy_changes.map((c, i) => (
              <li key={i} className="p-4 flex gap-3 text-sm">
                <span className="text-[#FF2A00] font-bold flex-shrink-0">▸</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {(fs.segment_performance || []).length > 0 && (
        <Panel title="Segment Performance">
          <div className="border-2 border-strong bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 border-b-2 border-strong">
                <tr className="text-[10px] uppercase tracking-[0.2em]">
                  <th className="text-left px-4 py-3">Segment</th>
                  <th className="text-right px-4 py-3">Revenue</th>
                  <th className="text-right px-4 py-3">YoY</th>
                  <th className="text-right px-4 py-3">Margin</th>
                </tr>
              </thead>
              <tbody>
                {fs.segment_performance.map((s, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td className="px-4 py-3 font-medium">{s.segment}</td>
                    <td className="px-4 py-3 text-right font-mono-data">{s.revenue}</td>
                    <td className={`px-4 py-3 text-right font-mono-data ${String(s.yoy || "").startsWith("-") ? "text-[#FF2A00]" : "text-[#00C853]"}`}>{s.yoy}</td>
                    <td className="px-4 py-3 text-right font-mono-data">{s.margin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function ManagementTab({ m }) {
  if (!m) return <EmptyPanel note="No management-commentary analysis returned." />;
  const credibility = Number(m.credibility_score || 0);
  return (
    <div className="space-y-8" data-testid="tab-panel-management">
      <div className="grid lg:grid-cols-3 gap-0 border-2 border-strong bg-white">
        <div className="p-6 border-r-0 lg:border-r-2 border-strong">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Tone</div>
          <div className="font-display font-bold text-2xl tracking-tight">{m.tone || "—"}</div>
          {m.tone_change_vs_prior && (
            <div className="text-xs text-zinc-600 mt-2">vs prior: {m.tone_change_vs_prior}</div>
          )}
          <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Credibility</div>
          <div className="flex items-baseline gap-1">
            <div className={`font-display font-black text-5xl tracking-tighter ${credibility < 40 ? "text-[#FF2A00]" : credibility < 70 ? "text-[#FFC800]" : "text-[#00C853]"}`}>
              {credibility}
            </div>
            <div className="text-base text-zinc-400">/100</div>
          </div>
          <div className="w-full h-1.5 bg-zinc-200 mt-3">
            <div className={`h-1.5 ${credibility < 40 ? "bg-[#FF2A00]" : credibility < 70 ? "bg-[#FFC800]" : "bg-[#00C853]"}`} style={{ width: `${credibility}%` }} />
          </div>
        </div>
        <div className="p-6 border-r-0 lg:border-r-2 border-strong">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Key Themes</div>
          <ul className="text-sm space-y-2 mb-6">
            {(m.key_themes || []).map((t, i) => (
              <li key={i} className="flex gap-2"><span className="text-[#002FA7]">▸</span>{t}</li>
            ))}
          </ul>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Buzzwords Overused</div>
          <div className="flex flex-wrap gap-2">
            {(m.buzzwords_overuse || []).map((b, i) => (
              <span key={i} className="text-xs border border-zinc-400 px-2 py-0.5">{b}</span>
            ))}
          </div>
        </div>
        <div className="p-6 bg-[#FEF2F2]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF2A00] mb-3">What They Didn&apos;t Say</div>
          <p className="text-sm text-zinc-800 leading-relaxed">{m.what_they_didnt_say || "—"}</p>
        </div>
      </div>
    </div>
  );
}

function FootnotesTab({ items }) {
  if (!items?.length) return <EmptyPanel note="No footnote findings surfaced." />;
  return (
    <div data-testid="tab-panel-footnotes">
      <div className="border-2 border-strong bg-white">
        <Accordion type="multiple" className="w-full">
          {items.map((d, i) => (
            <AccordionItem value={`item-${i}`} key={i} className="border-b border-zinc-200 last:border-b-0">
              <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-zinc-50 text-left">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{d.section}</div>
                    <div className="font-medium text-sm mt-0.5">{d.finding}</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-4 pl-12 text-sm text-zinc-700 leading-relaxed">
                <b>Significance:</b> {d.significance}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}

function ValuationTab({ v }) {
  if (!v) return <EmptyPanel note="No valuation view available for this report. Click 'Re-analyze' at the top to regenerate with the latest AI." />;

  // Detect legacy schema (single-panel valuation with current_multiples + key_assumptions)
  const isLegacy = !v.dcf && !v.relative_valuation && !v.asset_based && (v.current_multiples || v.key_assumptions);
  if (isLegacy) {
    return (
      <div className="space-y-6" data-testid="tab-panel-valuation">
        <div className="border-l-4 border-[#FFC800] bg-yellow-50 p-4 text-xs text-zinc-700">
          <b>Older analysis format detected.</b> Click <b>Re-analyze</b> at the top of the page to regenerate this report
          with the new 3-method valuation (DCF · Multiples · Asset-based) and a consensus recommendation.
        </div>
        <div className="grid lg:grid-cols-3 gap-0 border-2 border-strong bg-white">
          <div className="p-6 border-r-0 lg:border-r-2 border-strong">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Multiples</div>
            <ul className="text-sm space-y-3">
              {(v.current_multiples || []).map((m, i) => (
                <li key={i} className="border-b border-zinc-100 pb-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-zinc-700">{m.name}</span>
                    <span className="font-mono-data font-bold">{m.value}</span>
                  </div>
                  {m.vs_peers && <div className="text-xs text-zinc-500 mt-0.5">vs peers: {m.vs_peers}</div>}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 border-r-0 lg:border-r-2 border-strong">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Key Assumptions</div>
            <ul className="text-sm space-y-2">
              {(v.key_assumptions || []).map((k, i) => (
                <li key={i} className="flex gap-2"><span className="text-[#002FA7]">▸</span>{k}</li>
              ))}
            </ul>
          </div>
          <div className="p-6 flex flex-col justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Fair Value</div>
              <div className="font-display font-black text-2xl tracking-tighter">{v.fair_value_range || "—"}</div>
            </div>
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Verdict</div>
              <div className="inline-block bg-black text-white px-3 py-1 text-xs uppercase tracking-[0.2em]">{v.verdict || "—"}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const dcf = v.dcf || {};
  const rel = v.relative_valuation || {};
  const asset = v.asset_based || {};
  const cons = v.consensus || {};
  return (
    <div className="space-y-8" data-testid="tab-panel-valuation">
      {/* ── 3 METHOD CARDS ─────────────────────────────────── */}
      <div className="grid lg:grid-cols-3 gap-0 border-2 border-strong bg-white">
        <MethodCard
          title="DCF"
          subtitle="Discounted Cash Flow"
          verdict={dcf.verdict}
          confidence={dcf.confidence}
          rows={[
            ["Revenue growth", dcf.revenue_growth_assumption],
            ["Terminal growth", dcf.terminal_growth],
            ["WACC / Discount", dcf.discount_rate_wacc],
            ["FCF used", dcf.fcf_used],
            ["Implied value", dcf.implied_share_value, "highlight"],
            ["Current price", dcf.current_price],
            ["Upside", dcf.upside_pct, dcf.upside_pct && String(dcf.upside_pct).startsWith("-") ? "bad" : "good"],
          ]}
          notes={dcf.notes}
          border
        />
        <MethodCard
          title="Multiples"
          subtitle="Relative Valuation"
          verdict={rel.verdict}
          confidence={rel.confidence}
          rows={(rel.current_multiples || []).map((m) => [m.name, m.value + (m.sector_median ? ` (peer ${m.sector_median})` : ""), m.status])}
          extra={rel.peer_group ? [["Peer group", rel.peer_group]] : []}
          notes={rel.notes}
          border
        />
        <MethodCard
          title="Asset-Based"
          subtitle="Book Value / Graham"
          verdict={asset.verdict}
          confidence={asset.confidence}
          rows={[
            ["Book value / sh", asset.book_value_per_share],
            ["Tangible book / sh", asset.tangible_book_value],
            ["Graham number", asset.graham_number, "highlight"],
            ["NCAV", asset.ncav],
            ["Current price", asset.current_price],
            ["Premium to book", asset.premium_to_book_pct, asset.premium_to_book_pct && String(asset.premium_to_book_pct).startsWith("-") ? "good" : "bad"],
          ]}
          notes={asset.notes}
        />
      </div>

      {/* ── CONSENSUS ─────────────────────────────────────── */}
      {cons && (cons.fair_value_range || cons.weighted_verdict) && (
        <div className="border-2 border-strong bg-black text-white p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3">/ Consensus Fair Value</div>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Range</div>
              <div className="font-display font-black text-3xl tracking-tighter">{cons.fair_value_range || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Recommendation</div>
              <div className={`inline-block px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold ${recStyle(cons.recommendation)}`}>
                {cons.recommendation || cons.weighted_verdict || "—"}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">Conviction</div>
              <div className="flex items-baseline gap-1">
                <div className="font-mono-data font-bold text-2xl">{cons.conviction ?? "—"}</div>
                <div className="text-xs text-zinc-500">/100</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Rationale</div>
              <p className="text-sm leading-relaxed">{cons.rationale || "—"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MethodCard({ title, subtitle, verdict, confidence, rows, extra, notes, border }) {
  const verdictColor = {
    Cheap: "#00C853", Fair: "#002FA7", Expensive: "#FF2A00", Unknowable: "#A1A1AA",
  }[verdict] || "#A1A1AA";
  return (
    <div className={`p-6 flex flex-col ${border ? "border-r-0 lg:border-r-2 border-strong" : ""}`}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-display font-black text-2xl tracking-tighter">{title}</h3>
        <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 text-white" style={{ background: verdictColor }}>
          {verdict || "—"}
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">{subtitle}</div>
      <ul className="text-sm space-y-2 mb-4 flex-grow">
        {(rows || []).filter(([_, v]) => v != null && v !== "").map(([label, val, tone], i) => (
          <li key={i} className="flex items-baseline justify-between gap-3 border-b border-zinc-100 pb-1.5">
            <span className="text-zinc-500 text-xs">{label}</span>
            <span className={`font-mono-data text-right ${
              tone === "highlight" ? "font-bold text-black" :
              tone === "good" ? "text-[#00C853] font-bold" :
              tone === "bad" ? "text-[#FF2A00] font-bold" :
              "text-zinc-800"
            }`}>{val}</span>
          </li>
        ))}
        {(extra || []).map(([label, val], i) => (
          <li key={`e${i}`} className="flex flex-col gap-1 border-b border-zinc-100 pb-1.5">
            <span className="text-zinc-500 text-[10px] uppercase tracking-[0.2em]">{label}</span>
            <span className="text-xs">{val}</span>
          </li>
        ))}
      </ul>
      {notes && <div className="text-xs text-zinc-600 leading-relaxed">{notes}</div>}
      {confidence != null && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            <span>Confidence</span><span>{confidence}%</span>
          </div>
          <div className="w-full h-1 bg-zinc-200">
            <div className="h-1" style={{ width: `${confidence}%`, background: verdictColor }} />
          </div>
        </div>
      )}
    </div>
  );
}

function recStyle(rec) {
  return {
    Buy: "bg-[#00C853] text-black", Hold: "bg-white text-black",
    Reduce: "bg-[#FFC800] text-black", Avoid: "bg-[#FF2A00] text-white",
  }[rec] || "bg-zinc-700 text-white";
}

/* ─────────────────────────── HELPERS ─────────────────────────── */

function Stat({ label, value, accent }) {
  return (
    <div className="p-5 border-r border-strong last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="font-display font-black text-3xl tracking-tighter" style={accent ? { color: accent } : {}}>
        {value}
      </div>
    </div>
  );
}

function Panel({ title, count, accent, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {accent && <div className="w-3 h-3" style={{ background: accent }} />}
          <h2 className="font-display font-black text-xl sm:text-2xl tracking-tighter">{title}</h2>
        </div>
        {count !== undefined && <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{count} items</div>}
      </div>
      {children}
    </section>
  );
}

function FlagCard({ f }) {
  const sty = SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.medium;
  const conf = f.confidence != null ? Number(f.confidence) : null;
  const evType = (f.evidence_type || "").replace(/_/g, " ");
  return (
    <div
      className="bg-white border-l-4 border-2 border-strong p-5"
      style={{ borderLeftColor: sty.border, backgroundColor: sty.bg }}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <AlertTriangle className="w-4 h-4" style={{ color: sty.text }} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: sty.text }}>{sty.label}</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">· {f.category}</span>
        {conf != null && (
          <span className={`ml-auto text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 font-bold ${
            conf >= 85 ? "bg-[#00C853] text-black" :
            conf >= 70 ? "bg-[#002FA7] text-white" :
            "bg-zinc-500 text-white"
          }`}>
            {conf}% confidence
          </span>
        )}
      </div>
      <h3 className="font-display font-bold text-xl tracking-tight mb-2">{f.title}</h3>
      <p className="text-sm text-zinc-800 leading-relaxed mb-3">{f.description}</p>
      {f.evidence && (
        <div className="bg-white border border-zinc-300 p-3 text-xs font-mono-data text-zinc-700 mb-3">
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">EVIDENCE</div>
            {evType && <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-400">{evType}</div>}
          </div>
          {f.evidence}
        </div>
      )}
      {f.investor_impact && (
        <div className="text-xs text-zinc-700"><b>Investor Impact:</b> {f.investor_impact}</div>
      )}
    </div>
  );
}

function EmptyPanel({ note }) {
  return (
    <div className="border-2 border-dashed border-strong bg-white p-8 text-sm text-zinc-500">
      {note}
    </div>
  );
}

function Page({ children }) {
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">{children}</div>
    </div>
  );
}

function sevRank(s) {
  return { critical: 0, high: 1, medium: 2, low: 3 }[s] ?? 4;
}
